/**
 * Auto-detection for wallet connections (EIP-1193 providers)
 * Supports MetaMask, Coinbase Wallet, and other injected providers
 */

// Extend Window interface to include ethereum provider
declare global {
  interface Window {
    ethereum?: WalletProvider;
  }
}

export interface WalletProvider {
  request: (args: { method: string; params?: any[] }) => Promise<any>;
  on?: (event: string, handler: (...args: any[]) => void) => void;
  removeListener?: (event: string, handler: (...args: any[]) => void) => void;
  isMetaMask?: boolean;
  isCoinbaseWallet?: boolean;
}

export interface WalletInfo {
  address: string;
  chainId: number;
  walletType: string;
}

export type WalletEventHandler = (info: WalletInfo) => void;
export type WalletDisconnectHandler = () => void;

/**
 * Detects wallet type from provider
 */
function detectWalletType(provider: WalletProvider): string {
  if (provider.isMetaMask) return 'metamask';
  if (provider.isCoinbaseWallet) return 'coinbase';
  return 'injected';
}

/**
 * WalletDetector - Auto-detects wallet connections and changes
 *
 * Simple, auditable implementation:
 * 1. Checks for window.ethereum
 * 2. Listens to standard EIP-1193 events
 * 3. Calls handlers when wallets connect/disconnect
 */
export class WalletDetector {
  private provider: WalletProvider | null = null;
  private currentAddress: string | null = null;
  private listeners: {
    accountsChanged: ((accounts: string[]) => void) | null;
    chainChanged: ((chainId: string) => void) | null;
    disconnect: (() => void) | null;
  } = {
    accountsChanged: null,
    chainChanged: null,
    disconnect: null,
  };

  constructor(
    private onConnect: WalletEventHandler,
    private onDisconnect: WalletDisconnectHandler,
    private onChainChanged?: (chainId: number) => void
  ) {}

  /**
   * Start detecting wallet events
   */
  async start(): Promise<void> {
    // Check if window.ethereum exists
    if (typeof window === 'undefined' || !window.ethereum) {
      return;
    }

    this.provider = window.ethereum as WalletProvider;

    // Check if already connected
    await this.checkCurrentConnection();

    // Listen to events
    this.setupListeners();
  }

  /**
   * Stop detecting and clean up listeners
   */
  stop(): void {
    if (!this.provider || !this.provider.removeListener) return;

    // Remove all listeners
    if (this.listeners.accountsChanged) {
      this.provider.removeListener('accountsChanged', this.listeners.accountsChanged);
    }
    if (this.listeners.chainChanged) {
      this.provider.removeListener('chainChanged', this.listeners.chainChanged);
    }
    if (this.listeners.disconnect) {
      this.provider.removeListener('disconnect', this.listeners.disconnect);
    }

    this.listeners = {
      accountsChanged: null,
      chainChanged: null,
      disconnect: null,
    };
    this.currentAddress = null;
  }

  /**
   * Check if wallet is already connected
   */
  private async checkCurrentConnection(): Promise<void> {
    if (!this.provider) return;

    try {
      const accounts = await this.provider.request({
        method: 'eth_accounts',
      });

      if (accounts && accounts.length > 0) {
        await this.handleAccountsChanged(accounts);
      }
    } catch (error) {
      // Silently fail - wallet might not be unlocked
    }
  }

  /**
   * Setup event listeners for wallet changes
   */
  private setupListeners(): void {
    if (!this.provider || !this.provider.on) return;

    // Listen to account changes
    this.listeners.accountsChanged = (accounts: string[]) => {
      this.handleAccountsChanged(accounts);
    };
    this.provider.on('accountsChanged', this.listeners.accountsChanged);

    // Listen to chain changes
    this.listeners.chainChanged = (chainId: string) => {
      this.handleChainChanged(chainId);
    };
    this.provider.on('chainChanged', this.listeners.chainChanged);

    // Listen to disconnections
    this.listeners.disconnect = () => {
      this.handleDisconnect();
    };
    this.provider.on('disconnect', this.listeners.disconnect);
  }

  /**
   * Handle account changes (connect/disconnect/switch)
   */
  private async handleAccountsChanged(accounts: string[]): Promise<void> {
    if (!this.provider) return;

    // No accounts = disconnected
    if (!accounts || accounts.length === 0) {
      this.handleDisconnect();
      return;
    }

    const newAddress = accounts[0];

    // Only trigger if address actually changed
    if (newAddress !== this.currentAddress) {
      this.currentAddress = newAddress;

      try {
        // Get current chain ID
        const chainId = await this.provider.request({
          method: 'eth_chainId',
        });

        const walletInfo: WalletInfo = {
          address: newAddress,
          chainId: parseInt(chainId, 16),
          walletType: detectWalletType(this.provider),
        };

        this.onConnect(walletInfo);
      } catch (error) {
        // If we can't get chain ID, still connect with default
        this.onConnect({
          address: newAddress,
          chainId: 1, // Default to mainnet
          walletType: detectWalletType(this.provider),
        });
      }
    }
  }

  /**
   * Handle chain changes
   */
  private handleChainChanged(chainIdHex: string): void {
    const chainId = parseInt(chainIdHex, 16);

    if (this.onChainChanged) {
      this.onChainChanged(chainId);
    }
  }

  /**
   * Handle wallet disconnection
   */
  private handleDisconnect(): void {
    this.currentAddress = null;
    this.onDisconnect();
  }

  /**
   * Get current connected address
   */
  getCurrentAddress(): string | null {
    return this.currentAddress;
  }
}

/**
 * Check if wallet provider is available
 */
export function isWalletAvailable(): boolean {
  return typeof window !== 'undefined' && !!window.ethereum;
}

/**
 * Get wallet provider if available
 */
export function getWalletProvider(): WalletProvider | null {
  if (!isWalletAvailable()) return null;
  return window.ethereum as WalletProvider;
}
